import { formatGqlErrors } from 'common/GqlUtilities';
import { StorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import { useCallback } from 'react';
import {
  CurrentUserFieldsFragment,
  SegmentFilter,
  useBecomeATasterInfoQuery,
  useBecomeATasterMutation,
} from 'src/gqlReactTypings.generated.d';
import { useHistory } from 'src/shared/hooks/useHistory';
import Notifications from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useScopedReducer } from 'src/shef-global-state/shefGlobalState';

export interface IBecomeATasterOptions {
  goToExplore?: boolean;
}

interface IUseBecomeATasterArguments {
  zipCode?: string;
  deliveryDates?: string[];
  dietaryFilters?: string[];
}

export const useBecomeATaster = ({ zipCode, deliveryDates, dietaryFilters }: IUseBecomeATasterArguments = {}) => {
  // For logged out users, store whether they've accepted this modal locally. This is not used if the user is logged in
  const [isTasteTesterLocalStorage, setIsTasteTesterLocalStorage] = useLocalStorage<boolean>(
    StorageKeys.IS_TASTE_TESTER,
    false
  );

  const { data, loading, refetch } = useBecomeATasterInfoQuery({
    variables: {
      zipCode,
      deliveryDates: deliveryDates?.length ? deliveryDates : undefined,
    },
    skip: isTasteTesterLocalStorage,
  });
  const [becomeATasterMutation] = useBecomeATasterMutation();

  const isTasteTester = data?.me?.isTasteTester ?? isTasteTesterLocalStorage;
  const unlockedTrialShefDiscovery = data?.me?.unlockedTrialShefDiscovery ?? isTasteTesterLocalStorage;

  const isEligibleForTrialShefDiscovery = !isTasteTester && unlockedTrialShefDiscovery;

  const history = useHistory();
  const { reduce } = useScopedReducer('currentUser');
  const becomeATaster = useCallback(
    async ({ goToExplore }: IBecomeATasterOptions) => {
      setIsTasteTesterLocalStorage(true);
      let fallbackZipCode: string | null | undefined = null;
      if (data?.me) {
        const { data, errors } = await becomeATasterMutation();
        if (errors) {
          return Notifications.error(formatGqlErrors(errors));
        }

        const newCurrentUser: CurrentUserFieldsFragment | undefined = data?.becomeATaster;
        if (!newCurrentUser) {
          return Notifications.error('Something went wrong. Please contact support');
        }

        reduce(() => newCurrentUser);
        fallbackZipCode = newCurrentUser.lastSearchedZipCode;

        refetch().catch(console.error);
      }

      if (goToExplore) {
        const zipCodeForExplore = zipCode ?? fallbackZipCode;
        if (zipCodeForExplore) {
          const newPath = getSluggedPath(
            Routes.CONSUMER_EXPLORE,
            [{ slug: Slugs.ZIP_CODE, value: zipCodeForExplore }],
            {
              [QueryParams.SEGMENT_FILTER]: SegmentFilter.Trial,
              [QueryParams.DATE]: deliveryDates,
              [QueryParams.DIETARY_FILTER]: dietaryFilters,
            }
          );

          history.push(newPath);
        } else {
          history.push('/');
        }
      }
    },
    [
      setIsTasteTesterLocalStorage,
      data?.me,
      becomeATasterMutation,
      reduce,
      refetch,
      zipCode,
      deliveryDates,
      dietaryFilters,
      history,
    ]
  );

  return {
    loading,
    isTasteTester,
    unlockedTrialShefDiscovery,
    becomeATaster,
    isEligibleForTrialShefDiscovery,
  };
};
